import React, { useEffect, useState, useMemo } from 'react'
import { DialogContent, Typography, IconButton, Icon, MenuItem } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import StyledDatePicker from '../../Common/StyledDatePicker'
import StyledKeyboardTimePicker from '../../Common/StyledKeyboardTimePicker'
import CalendarIconButton from '../../Common/Buttons/CalendarIconButton'
import { MainButton } from '../../Button/MainButton'
import { TRAINING_PAGE_IDS, standardizeStartOfDateUTC } from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import TrainingSessionAddAttendeeDialog from './TrainingSessionAddAttendeeDialog'
import { ArrowBackIosOutlined, ArrowForwardIosOutlined, AddOutlined } from '@material-ui/icons'
import { gray } from '../../../utils/colors'
import { FONT_WEIGHTS } from '../../../utils'
import DangerConfirmationDialog from '../../Common/Dialogs/DangerConfirmation'
import StandardConfirmationDialog from '../../Common/Dialogs/StandardConfirmation'
import moment from 'moment'
import StyledAlert from '../../Common/StyledAlert'
import StyledTextField from '../../Common/StyledTextField'

const useStyles = makeStyles((theme) => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem',
		flex: 1
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div': {
			flex: 1
		},
		gap: '1.5rem'
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		},
		justifySelf: 'flex-end'
	},
	datePicker: {
		margin: 0,
		'& .MuiInputLabel-filled': {
			transform: 'translate(48px, 20px) scale(1)'
		},
		'& .MuiInputLabel-shrink': {
			transform: 'translate(48px, 10px) scale(0.75)'
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	attendeesHeaderContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '.45rem'
	},
	attendeesContainer: {
		overflow: 'scroll',
		border: `1px solid ${gray[400]}`,
		borderRadius: '.375rem',
		padding: '1rem',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start'
	},
	attendee: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '.4rem',
		'& > span': {
			fontSize: '1rem',
			fontWeight: 600
		}
	},
	sectionHeader: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		letterSpacing: '0.047rem'
	}
}))

/**
 *
 * @param {{
  open: Boolean, 
	onSaved: Function,
	onClose: Function, 
	onCancelled: Function,
	title: String,
	trainingClass: Object,
	session: Object,
}} params
 * @returns
 */
const TrainingSessionEditDialog = ({
	open,
	onSaved,
	onCancelled,
	onClose,
	title = 'Edit Session',
	trainingClass,
	session = {}
}) => {
	const classes = useStyles()
	const theme = useTheme()

	const [editedSession, setEditedSession] = useState(session)
	const [editedKeys, setEditedKeys] = useState(new Set())
	const [addAttendeeDialogOpen, setAddAttendeeDialogOpen] = useState(false)
	const [confirmRemoveAttendeeDialogOpen, setConfirmRemoveAttendeeDialogOpen] = useState(false)
	const [attendeeToRemove, setAttendeeToRemove] = useState(null)
	const [errors, setErrors] = useState([])
	const [loading, setLoading] = useState(false)
	const [confirmChangesDialogOpen, setConfirmChangesDialogOpen] = useState(false)

	const [isDateCalendarOpen, setIsDateCalendarOpen] = useState(false)

	const { facility, saveAttendaceStatusForClass, updateTrainingClassSession, trainingStaff } =
		useSelectedFacility()

	const isPrivate = trainingClass?.training_course_id?.categories?.includes('private') || false

	useEffect(() => {
		setEditedSession(session)
	}, [session, setEditedSession])

	const handleResetDialog = () => {
		setEditedSession({})
		setEditedKeys(new Set())
		setErrors([])
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleOnClosed = () => {
		if (onClose) {
			handleResetDialog()
			onClose()
		}
	}

	const handleCancelAddAttendeeRequest = () => {
		setAddAttendeeDialogOpen(false)
	}

	const handleOnConfirmChangesCancelled = () => {
		setConfirmChangesDialogOpen(false)
	}

	const handleOnConfirmChanges = () => {
		setConfirmChangesDialogOpen(true)
	}

	const confirmChangesRequired = () => {
		return editedKeys.has('new_date') || editedKeys.has('new_start_time')
	}

	const handleRemoveAttendeeRequest = (attendee) => {
		const remove = {
			...attendee,
			handler_ids: attendee.handlers.map((handler) => handler._id),
			handler_names: attendee.handlers.map((handler) => handler.full_name),
			pet_id: attendee.pet[0]._id,
			pet_name: attendee.pet[0].name
		}
		setAttendeeToRemove(remove)
		setConfirmRemoveAttendeeDialogOpen(true)
	}

	const handleCancelRemoveAttendeeRequest = () => {
		setConfirmRemoveAttendeeDialogOpen(false)
		setTimeout(() => {
			setAttendeeToRemove(null)
		}, 500)
	}

	const handleRemoveAttendee = async () => {
		const { training_attendance_id, pet_id, session } = attendeeToRemove || {}
		if (!training_attendance_id || !pet_id) {
			return
		}

		const { _id } = trainingClass
		setLoading(true)

		await saveAttendaceStatusForClass({
			facility_id: facility._id,
			mongo_class_id: _id,
			training_attendance_id: training_attendance_id,
			pet_id: pet_id,
			status_id: 7, // Absent
			session: session,
			source: TRAINING_PAGE_IDS.ClassView
		})
		setLoading(false)

		handleCancelRemoveAttendeeRequest()
	}

	const handleOnSave = async () => {
		let response = {}
		setConfirmChangesDialogOpen(false)
		if (editedKeys.size > 0) {
			const updates = {}

			for (const key of editedKeys) {
				updates[key] = editedSession[key]
			}

			updates['original_session_date'] = trainingClass?.dates[editedSession?.session - 1]
			updates['original_start_time'] = session.start_time
			updates['attendance_ids'] = session.attendance.map((attendee) => attendee.training_attendance_id)
			updates['adjustment_id'] = session.adjustment_id || null

			// Private classes can have more leeway with date sequence updates.
			if (isPrivate) {
				updates['is_private_class'] = true
			}

			setLoading(true)
			setErrors([])

			response = await updateTrainingClassSession({
				mongo_class_id: trainingClass?._id,
				updates,
				facility_id: facility?._id,
				session_number: editedSession?.session
			})

			setLoading(false)
		}

		if (onSaved && response?.success === true) {
			handleResetDialog()
			onSaved()
			setEditedKeys(new Set())
		}

		if (response?.success === false && response.errors) {
			setErrors(response.errors)
		}
	}

	const handleFieldUpdate = ({ field, targetValue }) => {
		const [mainField, subField] = field.split('.')
		setEditedSession((prev) => {
			const revised = { ...prev }
			if (subField) {
				revised[mainField] = { ...revised[mainField], [subField]: targetValue }
			} else {
				revised[field] = targetValue
			}

			if (field === 'new_start_time') {
				const { training_course_id } = trainingClass
				if (!training_course_id) {
					revised.new_end_time = ''
				}

				if (targetValue && /^\d{2}:\d{2}$/.test(targetValue)) {
					const { session_length } = training_course_id
					const [hours, minutes] = targetValue.split(':')
					const start = moment().hours(hours).minutes(minutes)
					const end = start.add(session_length, 'minutes')
					revised.new_end_time = end.format('HH:mm')
				}
			}

			return revised
		})
		setEditedKeys((keys) => keys.add(mainField))
		if (mainField === 'new_start_time') {
			setEditedKeys((keys) => keys.add('new_end_time'))
		}
	}

	const canSave = () => {
		if (editedKeys.has('new_start_time') && !editedSession.new_start_time) {
			return false
		}
		return editedKeys.size > 0
	}

	const pendingAttendees = useMemo(() => {
		return session?.attendance?.filter((attendee) => attendee.status.id === 1) || []
	}, [session])

	const checkedInAttendees = useMemo(() => {
		return session?.attendance?.filter((attendee) => attendee.status.id === 4) || []
	}, [session])

	const availableCapacity = useMemo(() => {
		return (trainingClass?.capacity_max || 0) - checkedInAttendees.length - pendingAttendees.length
	}, [trainingClass, checkedInAttendees, pendingAttendees])

	const sessionIsAtMaxCapacity = useMemo(() => {
		if (isPrivate) {
			return session?.attendance?.length === trainingClass?.capacity_max
		}
		return checkedInAttendees.length >= trainingClass?.capacity_max
	}, [checkedInAttendees, trainingClass, isPrivate, session])

	return (
		<>
			<StyledDialog
				open={open}
				onClose={handleOnClosed}
				title={title?.toUpperCase()}
				showActivityIndicator={loading}
				width={577}
				maxWidth={577}
				minWidth={577}>
				<DialogContent style={{ display: 'flex' }}>
					<div className={classes.dialogContent}>
						<div className={classes.rowWrapper}>
							<div className={classes.row}>
								<div>
									<StyledDatePicker
										disableDatePickerValidationErrors={true}
										open={isDateCalendarOpen}
										onOpen={() => setIsDateCalendarOpen(true)}
										onClose={() => setIsDateCalendarOpen(false)}
										InputProps={{
											startAdornment: (
												<CalendarIconButton onClicked={setIsDateCalendarOpen} />
											)
										}}
										disablePast={true}
										variant="inline"
										format="MM/DD/YYYY"
										margin="normal"
										id="session-date"
										header="Date"
										label={'Date'}
										inputVariant="filled"
										value={
											editedSession.new_date
												? standardizeStartOfDateUTC(editedSession.new_date)
												: standardizeStartOfDateUTC(editedSession.session_date)
										}
										className={classes.datePicker}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'new_date',
												targetValue: e.format('YYYY-MM-DD')
											})
										}
										rightArrowIcon={
											<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
										}
										leftArrowIcon={
											<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />
										}
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledKeyboardTimePicker
										disableFuture
										ampm={false}
										format="HH:mm"
										label="Time"
										header="Time (24hr format)"
										initialvalue={editedSession.start_time}
										timeChanged={(e) => {
											const { formatted } = e
											handleFieldUpdate({
												field: 'new_start_time',
												targetValue: formatted
											})
										}}
									/>
								</div>
							</div>
							{editedKeys.has('new_start_time') ? (
								<div className={classes.row}>
									<div>
										<StyledKeyboardTimePicker
											disableFuture
											ampm={false}
											format="HH:mm"
											label="Class End Time"
											header="Class End Time"
											initialvalue={editedSession.new_end_time}
											disabled
											required
										/>
									</div>
								</div>
							) : null}
							<div>
								<StyledTextField
									variant="filled"
									label="Instructor"
									header="Instructor"
									select
									value={editedSession.new_instructor_id || editedSession.instructor_id}
									onChange={(e) =>
										handleFieldUpdate({
											field: 'new_instructor_id',
											targetValue: e.target.value
										})
									}>
									{trainingStaff.map(({ _id, name }) => {
										return (
											<MenuItem key={_id} value={_id}>
												{name}
											</MenuItem>
										)
									})}
								</StyledTextField>
							</div>
							<div>
								<div className={classes.attendeesHeaderContainer}>
									<Typography variant="h3" className={classes.sectionHeader}>
										Attendees ({availableCapacity} / {trainingClass?.capacity_max} slots
										available)
									</Typography>
									<MainButton
										label="Add Attendee"
										variant="text"
										disabled={sessionIsAtMaxCapacity}
										onClick={() => setAddAttendeeDialogOpen(true)}
										style={{ padding: `0 .5rem`, fontSize: '.85rem' }}
										startIcon={
											<AddOutlined
												style={{
													marginRight: 0,
													fill: sessionIsAtMaxCapacity
														? 'gray'
														: theme.palette.secondary.main
												}}
											/>
										}
									/>
								</div>
								<div className={classes.attendeesContainer}>
									{session?.attendance
										? session.attendance.map((attendance) => {
												const { status } = attendance
												const disabled = [4, 7, 8, 9, 10].includes(status.id)
												return (
													<div
														className={classes.attendee}
														key={attendance._id}
														style={{ opacity: disabled ? 0.5 : 1 }}>
														<IconButton
															disabled={disabled}
															onClick={() =>
																handleRemoveAttendeeRequest(attendance)
															}>
															<Icon
																className="fas fa-minus-circle"
																style={{
																	fontSize: '1rem',
																	overflow: 'unset',
																	color: 'red'
																}}
															/>
														</IconButton>
														<Typography variant="inherit">
															{attendance.handlers
																.map((handler) => handler.full_name)
																.join(', ')}{' '}
															({attendance.pet[0].name}){' '}
															{disabled ? status.name : ''}
														</Typography>
													</div>
												)
										  })
										: null}
								</div>
							</div>
							{errors.length > 0 ? (
								<div>
									{errors.map((error, i) => {
										const { message = 'Error Saving' } = error
										return <StyledAlert key={`error-${i}`} type="error" text={message} />
									})}
								</div>
							) : null}
						</div>
						<div className={classes.dialogActions}>
							<MainButton
								label="SAVE"
								onClick={confirmChangesRequired() ? handleOnConfirmChanges : handleOnSave}
								variant="contained"
								disabled={!canSave()}
							/>
							<MainButton label="Cancel" onClick={handleOnCancelled} />
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
			<TrainingSessionAddAttendeeDialog
				open={addAttendeeDialogOpen}
				onClose={handleCancelAddAttendeeRequest}
				onSaved={handleCancelAddAttendeeRequest}
				onCancelled={handleCancelAddAttendeeRequest}
				title="Add Attendee"
				showActivityIndicator={loading}
				trainingClass={trainingClass}
				session={session}
			/>
			<DangerConfirmationDialog
				title="Remove Attendee"
				open={confirmRemoveAttendeeDialogOpen}
				description={
					<span>
						This will mark{' '}
						{attendeeToRemove?.handler_names?.length > 1 ? (
							<strong>{attendeeToRemove?.handler_names.join(', ')}</strong>
						) : (
							<strong>{attendeeToRemove?.handler_names?.[0]} </strong>
						)}{' '}
						and <strong>{attendeeToRemove?.pet_name}</strong> as absent from{' '}
						<strong>session {attendeeToRemove?.session}</strong>.
					</span>
				}
				dangerButtonText="Confirm"
				onConfirmDangerousAction={handleRemoveAttendee}
				onCancel={handleCancelRemoveAttendeeRequest}
			/>
			<StandardConfirmationDialog
				title="Confirm Session Changes"
				open={confirmChangesDialogOpen}
				onConfirm={handleOnSave}
				onCancel={handleOnConfirmChangesCancelled}
				description="Rescheduling a session will send an automated email to all pet parents in this class letting them know that the session has been rescheduled."
			/>
		</>
	)
}

export default TrainingSessionEditDialog
